import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Box from "../../../setupPage/SharedComponents/Box";
import SetupTittle from "../../../setupPage/SharedComponents/SetupTittle";
import {
  crmGetQueries,
  crmGetStaff,
  crmImportQueries,
  crmSaveQuery,
  mapQueryCsv,
  storeCrmLeadPrefill,
} from "../../../../api/crmApi";
import {
  AttendBar,
  AttachmentList,
  CrmDrawer,
  Field,
  FollowUpLog,
  GhostBtn,
  ImportCsvButton,
  LEAD_SOURCES,
  PrimaryBtn,
  ROUTES,
  STAGES,
  SelectInput,
  StaffSearchSelect,
  TextInput,
} from "./crmShared";

const empty = {
  customerName: "",
  phone: "",
  email: "",
  address: "",
  productType: "Mango",
  source: "WhatsApp",
  stage: "New Lead",
  route: "",
  weightKg: "",
  cost: "",
  boxCount: "",
  notes: "",
};

const CrmQueries = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("");
  const [editing, setEditing] = useState(null);
  const [staff, setStaff] = useState([]);

  const load = async () => {
    const data = await crmGetQueries({ search, stage, page: 1, pageSize: 200 });
    setRows(data.items);
  };

  useEffect(() => {
    load().catch(() => setRows([]));
    crmGetStaff().then(setStaff).catch(() => setStaff([]));
  }, [search, stage]);

  const save = async (e) => {
    e.preventDefault();
    await crmSaveQuery({
      ...editing,
      queryDate: editing.queryDate || editing.QueryDate || new Date().toISOString(),
      weightKg: editing.weightKg === "" ? null : Number(editing.weightKg),
      cost: editing.cost === "" ? null : Number(editing.cost),
      boxCount: editing.boxCount === "" ? null : Number(editing.boxCount),
      amount: editing.amount === "" ? null : Number(editing.amount),
    });
    setEditing(null);
    await load();
  };

  return (
    <Box top>
      <SetupTittle text="Leads" />
      <p className="text-sm text-stone-500 mb-3 max-w-3xl">
        Add a customer who contacted you on social media or by phone. Log every call, then create a
        walk-in booking when they confirm.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          placeholder="Search name, phone, invoice"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-stone-300 rounded-xl h-10 px-3 bg-white"
        />
        <SelectInput value={stage} onChange={setStage} options={STAGES} />
        <PrimaryBtn onClick={() => setEditing({ ...empty })}>New lead</PrimaryBtn>
        <ImportCsvButton
          label="Import CSV"
          onParsed={async (text) => {
            const mapped = mapQueryCsv(text);
            const result = await crmImportQueries(mapped);
            Swal.fire({
              icon: "success",
              title: `Imported ${result.imported ?? result.Imported ?? 0}`,
              text: result.failed || result.Failed ? `${result.failed || result.Failed} failed` : "",
            });
            await load();
          }}
        />
      </div>
      <div className="overflow-x-auto bg-white rounded-2xl border border-stone-200">
        <table className="min-w-full text-sm">
          <thead className="bg-orange-100">
            <tr>
              {["Date", "Customer", "Phone", "Product", "Source", "Owner", "Route", "Stage", "Boxes", "Cost"].map((h) => (
                <th key={h} className="px-3 py-2 text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id || r.Id}
                className="border-t border-stone-100 hover:bg-orange-50 cursor-pointer"
                onClick={() => setEditing(r)}
              >
                <td className="px-3 py-2">{(r.queryDate || r.QueryDate || "").toString().slice(0, 10)}</td>
                <td className="px-3 py-2">{r.customerName || r.CustomerName}</td>
                <td className="px-3 py-2">{r.phone || r.Phone}</td>
                <td className="px-3 py-2">{r.productType || r.ProductType}</td>
                <td className="px-3 py-2">{r.source || r.Source}</td>
                <td className="px-3 py-2">{r.ownerName || r.OwnerName}</td>
                <td className="px-3 py-2">{r.route || r.Route}</td>
                <td className="px-3 py-2">{r.stage || r.Stage}</td>
                <td className="px-3 py-2">{r.boxCount || r.BoxCount}</td>
                <td className="px-3 py-2">{r.cost || r.Cost || r.amount || r.Amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing ? (
        <CrmDrawer
          wide={!!(editing.id || editing.Id)}
          title={editing.id || editing.Id ? editing.customerName || editing.CustomerName || "Lead" : "New lead"}
          onClose={() => setEditing(null)}
          footer={(
            <>
              <PrimaryBtn onClick={save}>Save</PrimaryBtn>
              <GhostBtn onClick={() => setEditing(null)}>Cancel</GhostBtn>
              {editing.id || editing.Id ? (
                <PrimaryBtn
                  onClick={async () => {
                    const id = editing.id || editing.Id;
                    await crmSaveQuery({
                      ...editing,
                      id,
                      stage: "Confirmed",
                    });
                    storeCrmLeadPrefill({
                      queryId: id,
                      senderName: editing.customerName || editing.CustomerName || "",
                      senderPhone: editing.phone || editing.Phone || "",
                      senderEmail: editing.email || editing.Email || "",
                      senderAddress: editing.address || editing.Address || "",
                      itemDescription: editing.productType || editing.ProductType || "",
                      route: editing.route || editing.Route || "",
                    });
                    navigate("/admin-dashboard/walk-in-booking", {
                      state: { crmLeadQueryId: id },
                    });
                  }}
                >
                  Create booking
                </PrimaryBtn>
              ) : null}
            </>
          )}
        >
          {editing.id || editing.Id ? (
            <AttendBar
              relatedType="query"
              relatedId={editing.id || editing.Id}
              phone={editing.phone || editing.Phone}
            />
          ) : null}
          <div className={editing.id || editing.Id ? "grid grid-cols-1 lg:grid-cols-2 gap-6" : ""}>
            <form className="grid grid-cols-1 sm:grid-cols-2 gap-3" onSubmit={save}>
              <Field label="Customer">
                <TextInput value={editing.customerName ?? editing.CustomerName} onChange={(v) => setEditing({ ...editing, customerName: v })} />
              </Field>
              <Field label="Phone">
                <TextInput value={editing.phone ?? editing.Phone} onChange={(v) => setEditing({ ...editing, phone: v })} />
              </Field>
              <Field label="Email">
                <TextInput value={editing.email ?? editing.Email} onChange={(v) => setEditing({ ...editing, email: v })} />
              </Field>
              <Field label="Product">
                <TextInput value={editing.productType ?? editing.ProductType} onChange={(v) => setEditing({ ...editing, productType: v })} />
              </Field>
              <Field label="Source">
                <SelectInput value={editing.source ?? editing.Source} onChange={(v) => setEditing({ ...editing, source: v })} options={LEAD_SOURCES} />
              </Field>
              <Field label="Stage">
                <SelectInput value={editing.stage ?? editing.Stage} onChange={(v) => setEditing({ ...editing, stage: v })} options={STAGES} allowEmpty={false} />
              </Field>
              <Field label="Route">
                <SelectInput value={editing.route ?? editing.Route} onChange={(v) => setEditing({ ...editing, route: v })} options={ROUTES} />
              </Field>
              <div className="sm:col-span-2">
                <p className="font-semibold text-sm text-stone-700">Assign to</p>
                <div className="mt-1">
                  <StaffSearchSelect
                    staff={staff}
                    selectedIds={
                      editing.ownerUserId || editing.OwnerUserId
                        ? [editing.ownerUserId || editing.OwnerUserId]
                        : []
                    }
                    onChange={(ids) => {
                      const id = ids[ids.length - 1] || "";
                      const u = staff.find((s) => (s.id || s.Id) === id);
                      setEditing({
                        ...editing,
                        ownerUserId: id,
                        ownerName: u?.userName || u?.UserName || "",
                      });
                    }}
                    placeholder="Search staff"
                  />
                </div>
              </div>
              <Field label="Kg">
                <TextInput type="number" value={editing.weightKg ?? editing.WeightKg} onChange={(v) => setEditing({ ...editing, weightKg: v })} />
              </Field>
              <Field label="Boxes">
                <TextInput type="number" value={editing.boxCount ?? editing.BoxCount} onChange={(v) => setEditing({ ...editing, boxCount: v })} />
              </Field>
              <Field label="Quoted cost">
                <TextInput type="number" value={editing.cost ?? editing.Cost} onChange={(v) => setEditing({ ...editing, cost: v })} />
              </Field>
              <Field label="Address">
                <TextInput value={editing.address ?? editing.Address} onChange={(v) => setEditing({ ...editing, address: v })} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Notes">
                  <TextInput value={editing.notes ?? editing.Notes} onChange={(v) => setEditing({ ...editing, notes: v })} />
                </Field>
              </div>
              {editing.id || editing.Id ? (
                <div className="sm:col-span-2">
                  <AttachmentList relatedType="query" relatedId={editing.id || editing.Id} />
                </div>
              ) : null}
            </form>
            {editing.id || editing.Id ? (
              <div className="lg:border-l lg:border-stone-200 lg:pl-6">
                <FollowUpLog queryId={editing.id || editing.Id} />
              </div>
            ) : null}
          </div>
        </CrmDrawer>
      ) : null}
    </Box>
  );
};

export default CrmQueries;
